import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GetAdminAuditLogAPI } from '../../../../api/AdminAPI';

const DEFAULT_PAGINATION = { total: 0, page: 1, limit: 20, pages: 1 };
const DEFAULT_SUMMARY = { total: 0, last_24h: 0 };
const SEARCH_DEBOUNCE_MS = 400;

/**
 * useAdminAudit
 *
 * Hook responsável pela tela de Auditoria do Admin Geral: lista paginada/filtrada de
 * platform_admin_audit_log (list.php). Filtros (ação, empresa, busca, período, página)
 * vivem na URL via useSearchParams, no mesmo padrão de useAdminLogs, para que o link
 * possa ser compartilhado.
 *
 * @returns {Object} estado e ações da tela
 */
function useAdminAudit() {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback((updates, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Filtros (na URL) ───────────────────────────────────────────────────────
  const action = searchParams.get('action') || '';
  const company = searchParams.get('company') ?? '';
  const q = searchParams.get('search') || '';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';
  const page = parseInt(searchParams.get('page'), 10) || 1;

  const setAction = useCallback((value) => updateParams({ action: value }), [updateParams]);
  const setDateFrom = useCallback((value) => updateParams({ date_from: value }), [updateParams]);
  const setDateTo = useCallback((value) => updateParams({ date_to: value }), [updateParams]);
  const setPage = useCallback((value) => updateParams({ page: value }, false), [updateParams]);

  // ─── Empresa / busca (draft local + debounce -> URL) ───────────────────────
  const [companyInput, setCompanyInputState] = useState(company);
  const [searchInput, setSearchInputState] = useState(q);
  const companyTimer = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => { setCompanyInputState(company); }, [company]);
  useEffect(() => { setSearchInputState(q); }, [q]);

  const setCompanyInput = useCallback((value) => {
    setCompanyInputState(value);
    if (companyTimer.current) clearTimeout(companyTimer.current);
    companyTimer.current = setTimeout(() => updateParams({ company: value }), SEARCH_DEBOUNCE_MS);
  }, [updateParams]);

  const setSearchInput = useCallback((value) => {
    setSearchInputState(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => updateParams({ search: value }), SEARCH_DEBOUNCE_MS);
  }, [updateParams]);

  useEffect(() => () => {
    if (companyTimer.current) clearTimeout(companyTimer.current);
    if (searchTimer.current) clearTimeout(searchTimer.current);
  }, []);

  // ─── Lista (list.php) ───────────────────────────────────────────────────────
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [actions, setActions] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEntries = useCallback(() => {
    setIsLoading(true);
    setError(null);
    GetAdminAuditLogAPI(
      { action, company, search: q, date_from: dateFrom, date_to: dateTo, page, limit: DEFAULT_PAGINATION.limit },
      (response) => {
        if (!response || response.success === false) {
          setError(response?.error || 'Erro ao carregar o log de auditoria. Tente novamente.');
          setIsLoading(false);
          return;
        }
        setEntries(response.entries || []);
        setPagination(response.pagination || { ...DEFAULT_PAGINATION, page });
        setActions(response.actions || []);
        setSummary(response.summary || DEFAULT_SUMMARY);
        setIsLoading(false);
      }
    );
  }, [action, company, q, dateFrom, dateTo, page]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return {
    entries,
    pagination,
    actions,
    summary,
    isLoading,
    error,

    action,
    setAction,
    companyInput,
    setCompanyInput,
    searchInput,
    setSearchInput,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    setPage,

    refresh: fetchEntries,
  };
}

export default useAdminAudit;
